import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Download, Save, Clock, Database, FileSpreadsheet,
  CheckCircle, Settings, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const BackupPage = () => {
  const [config, setConfig] = useState({
    enabled: true,
    frequency: 'DIARIO',
    hour: 2,
    backup_email: '',
    include_vaccinations: true,
    include_patients: true,
    include_inventory: true,
    include_cash: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/backup/config`, {
        withCredentials: true
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching backup config:', error);
      if (error.response?.status !== 403) {
        toast.error('Error al cargar la configuración');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/backup/config`, config, {
        withCredentials: true
      });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateBackup = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/backup/generate`, {}, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `backup_vacunatrack_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Backup generado y descargado');
      fetchConfig(); // Refresh to get updated last_backup
    } catch (error) {
      toast.error('Error al generar el backup');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleString('es-CL');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="backup-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 heading">Backup de Datos</h1>
          <p className="text-slate-500 mt-1">
            Configuración de respaldos automáticos del sistema
          </p>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Generar Backup Ahora
            </CardTitle>
            <CardDescription>
              Descarga un archivo Excel con todos los datos del sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">
                  Último backup: <span className="font-medium">{formatDate(config.last_backup)}</span>
                </p>
              </div>
              <Button 
                onClick={handleGenerateBackup}
                disabled={generating}
                className="gap-2"
                data-testid="generate-backup-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
                {generating ? 'Generando...' : 'Descargar Backup'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración de Backup Automático
            </CardTitle>
            <CardDescription>
              Configura cuándo y qué datos incluir en los backups automáticos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Backup Automático</Label>
                <p className="text-sm text-slate-500">
                  Genera backups automáticamente según la frecuencia configurada
                </p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
                data-testid="toggle-enabled"
              />
            </div>

            {config.enabled && (
              <>
                {/* Frequency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frecuencia</Label>
                    <Select 
                      value={config.frequency}
                      onValueChange={(value) => setConfig({...config, frequency: value})}
                    >
                      <SelectTrigger data-testid="select-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DIARIO">Diario</SelectItem>
                        <SelectItem value="SEMANAL">Semanal</SelectItem>
                        <SelectItem value="MENSUAL">Mensual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Hora del día</Label>
                    <Select 
                      value={config.hour.toString()}
                      onValueChange={(value) => setConfig({...config, hour: parseInt(value)})}
                    >
                      <SelectTrigger data-testid="select-hour">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 24}, (_, i) => (
                          <SelectItem key={i} value={i.toString()}>
                            {i.toString().padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label>Email para notificaciones (opcional)</Label>
                  <Input
                    type="email"
                    value={config.backup_email || ''}
                    onChange={(e) => setConfig({...config, backup_email: e.target.value})}
                    placeholder="correo@ejemplo.cl"
                    data-testid="input-email"
                  />
                  <p className="text-xs text-slate-500">
                    Recibirás una notificación cuando se complete el backup
                  </p>
                </div>

                {/* Data to include */}
                <div className="space-y-4">
                  <Label className="text-base">Datos a incluir</Label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="font-medium">Vacunaciones</p>
                          <p className="text-xs text-slate-500">Registros de vacunación</p>
                        </div>
                      </div>
                      <Switch
                        checked={config.include_vaccinations}
                        onCheckedChange={(checked) => setConfig({...config, include_vaccinations: checked})}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="font-medium">Inventario</p>
                          <p className="text-xs text-slate-500">Stock de vacunas</p>
                        </div>
                      </div>
                      <Switch
                        checked={config.include_inventory}
                        onCheckedChange={(checked) => setConfig({...config, include_inventory: checked})}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-amber-500" />
                        <div>
                          <p className="font-medium">Caja Diaria</p>
                          <p className="text-xs text-slate-500">Registros financieros</p>
                        </div>
                      </div>
                      <Switch
                        checked={config.include_cash}
                        onCheckedChange={(checked) => setConfig({...config, include_cash: checked})}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-purple-500" />
                        <div>
                          <p className="font-medium">Pacientes</p>
                          <p className="text-xs text-slate-500">Datos de pacientes</p>
                        </div>
                      </div>
                      <Switch
                        checked={config.include_patients}
                        onCheckedChange={(checked) => setConfig({...config, include_patients: checked})}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="gap-2"
                data-testid="save-config-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Información sobre los backups</p>
                <ul className="mt-2 text-sm text-blue-700 space-y-1">
                  <li>• Los backups se generan en formato Excel (.xlsx)</li>
                  <li>• Cada hoja del Excel contiene una tabla diferente del sistema</li>
                  <li>• Los backups automáticos se ejecutan en la hora configurada</li>
                  <li>• Siempre puedes generar un backup manual con el botón de arriba</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BackupPage;
